import { Architecture, Close, Remove, RocketLaunch } from '@mui/icons-material';
import { Badge, Box, CircularProgress, Fab, IconButton, Portal, Tooltip } from '@mui/material';
import { RefObject, useCallback, useContext, useEffect, useRef, useState } from 'react';

import PromptChat, { PromptChatStatus } from './PromptChat';
import AuthContext from '../AuthContext';
import { usePersistedSize } from '../../common/hooks/usePersistedSize';
import { useKeyDown } from '../../common/hooks/useKeyDown';
import { useCurrentUserPermissions } from '../CurrentUserPermissionsContext';
import { Permissions } from '../../common/Permissions';
import ArchitectDeploymentDialog from './ArchitectDeploymentDialog';
import { designerChatAgentIds, designerChatScopes, useDesignerChatAdapter } from './useDesignerChatAdapter';
import { useArchitectDock } from './useArchitectDock';
import { useDesignerScreenAttributes } from './DesignerScreenContext';
import ArchitectContextIndicator, { ArchitectContextButton } from './ArchitectContextIndicator';
import usePromptChatContext from './usePromptChatContext';
import { appShape } from '../../themes/shapeTokens';

type Props = {
	expanded: boolean;
	onMinimize: () => void;
	onExpand: () => void;
	onDismiss?: () => void;
	visible?: boolean;
	contentRef?: RefObject<HTMLElement>;
};

/** Keep PromptChat mounted while minimized so drafts, progress and sessions survive. */
export default function ArchitectAssistant({ expanded, onMinimize, onExpand, onDismiss, visible = true, contentRef }: Props) {
	const auth = useContext(AuthContext);
	const getScreenAttributes = useDesignerScreenAttributes();
	const { hasPermission } = useCurrentUserPermissions();
	const [status, setStatus] = useState<PromptChatStatus>({ busy: false, failed: false });
	const [unread, setUnread] = useState(false);
	const [deploymentOpen, setDeploymentOpen] = useState(false);
	const context = usePromptChatContext({ Indicator: ArchitectContextIndicator, Button: ArchitectContextButton });
	const bubbleRef = useRef<HTMLButtonElement>(null);
	const sessionRef = useRef<string>();
	const dock = useArchitectDock(`architect.panel.corner:${auth.account?.homeAccountId ?? auth.account?.localAccountId ?? 'current-user'}`, contentRef);
	const { ref: panelRef, size } = usePersistedSize(`architect.panel.size:${auth.account?.homeAccountId ?? auth.account?.localAccountId ?? 'current-user'}`, expanded, { width: '480px', height: '740px' });
	useEffect(() => {
		if (!visible) return;
		if (expanded) setUnread(false);
		const frame = requestAnimationFrame(() => {
			if (expanded) (panelRef.current?.querySelector<HTMLElement>('textarea:not([disabled]), input:not([disabled])') ?? panelRef.current)?.focus();
			else bubbleRef.current?.focus();
		});
		return () => cancelAnimationFrame(frame);
	}, [expanded, visible, panelRef]);
	const handleEscape = useCallback((event: KeyboardEvent) => {
		if (event.defaultPrevented || deploymentOpen || document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]')) return;
		if (!panelRef.current?.contains(document.activeElement)) return;
		event.preventDefault();
		onMinimize();
	}, [deploymentOpen, onMinimize, panelRef]);
	useKeyDown(['Escape'], handleEscape, expanded);
	const handleMessage = useCallback(() => { if (!expanded) setUnread(true); }, [expanded]);
	const handleStatus = useCallback((next: PromptChatStatus) => {
		if (sessionRef.current !== next.sessionId) setUnread(false);
		sessionRef.current = next.sessionId;
		setStatus(next);
	}, []);
	const bubbleLabel = status.failed ? 'Architect needs attention' : status.busy ? 'Architect is working' : unread ? 'Architect has a new reply' : 'Open Architect';
	const adapter = useDesignerChatAdapter({
		scopeName: designerChatScopes.architect,
		messageAttributes: getScreenAttributes,
		agentId: designerChatAgentIds.architect,
		isAvailable: true,
		missingTargetMessage: 'Architect assistance is unavailable.',
	});
	return (
		<Portal>
			<Box
				ref={panelRef}
				role="region"
				aria-label="Randol Architect Assistant"
				tabIndex={-1}
				{...dock.dragHandlers}
				onPointerDown={(event) => {
					const target = event.target as HTMLElement;
					if (event.currentTarget.contains(target) && target.closest('header') && !target.closest('button, input, textarea, a, [role="button"]')) dock.dragHandlers.onPointerDown(event);
				}}
				sx={{
					...dock.position,
					display: expanded && visible ? 'flex' : 'none', flexDirection: 'column',
					width: size.width, maxWidth: dock.maxWidth,
					height: size.height, maxHeight: dock.maxHeight, minWidth: Math.min(340, dock.maxWidth), minHeight: Math.min(360, dock.maxHeight),
					resize: { xs: 'none', sm: 'both' }, overflow: 'hidden',
					borderRadius: appShape.surfaceRadius,
					border: 'none',
					boxShadow: 'none',
					bgcolor: 'transparent',
					backgroundImage: 'none',
					zIndex: (theme) => theme.zIndex.drawer + 1,
				}}
			>
				<Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
					<PromptChat adapter={adapter} defaultTitle="Randol Architect Assistant"
						{...context}
						showConversationNameBar
						headerIcon={<Architecture fontSize="small" color="primary" />}
						headerSx={{ bgcolor: 'background.paper', backgroundImage: 'var(--Paper-overlay, none)', py: 0.5, pl: 2, pr: 1, cursor: 'grab', touchAction: 'none', userSelect: 'none' }}
						headerPrimaryActions={hasPermission(Permissions.EnvironmentRead) && hasPermission(Permissions.EnvironmentDeploy)
							? <Tooltip title="Review deployment"><span><IconButton size="small" aria-label="Review Architect deployment" onClick={() => setDeploymentOpen(true)} disabled={status.busy} sx={{ color: 'text.secondary' }}><RocketLaunch fontSize="small" /></IconButton></span></Tooltip>
							: undefined}
						headerActions={<Tooltip title="Minimize to bubble"><IconButton size="small" aria-label="Minimize Architect" onClick={onMinimize} sx={{ color: 'text.secondary' }}><Remove fontSize="small" /></IconButton></Tooltip>}
						onStatusChange={handleStatus} onAssistantMessageReceived={handleMessage} emptyStateText="Ask me to help design your Apps, services, Flows or content." />
				</Box>
			</Box>
			{!expanded && visible && <Box {...dock.dragHandlers} sx={{
				...dock.bubblePosition, width: 'max-content',
				zIndex: (theme) => theme.zIndex.drawer + 1, touchAction: 'none',
				// No interpolation while following the pointer; animate only the corner snap.
				transition: dock.isDragging ? 'none' : 'left 400ms cubic-bezier(0.4, 0, 0.2, 1), bottom 400ms cubic-bezier(0.4, 0, 0.2, 1), transform 400ms cubic-bezier(0.4, 0, 0.2, 1)',
			}}>
			<Tooltip title={`${bubbleLabel} · Drag to move, or Alt + Left/Right to dock`}><Fab ref={bubbleRef} color={status.failed ? 'error' : 'primary'} aria-label={bubbleLabel} onClick={() => { if (dock.canClick()) onExpand(); }}
				onKeyDown={(event) => { if (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) { event.preventDefault(); dock.dock(event.key === 'ArrowLeft' ? 'left' : 'right'); } }}>
				<Badge variant="dot" color="warning" invisible={!unread}><Architecture /></Badge>
				{status.busy && <CircularProgress aria-label="Architect is working" size={64} sx={{ position: 'absolute' }} />}
			</Fab></Tooltip>
			{onDismiss && <Tooltip title="Dismiss Architect bubble"><IconButton data-no-drag aria-label="Dismiss Architect bubble" onClick={onDismiss}
				sx={{ position: 'absolute', top: -10, right: -10, width: 24, height: 24, bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'action.selected' } }}><Close sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
			</Box>}
			{/* MUI sizing maps numeric 1 to 100%, so visually hidden dimensions must use explicit pixels. */}
			<Box role="status" aria-live="polite" sx={{ position: 'fixed', top: 0, left: 0, width: '1px', height: '1px', overflow: 'hidden', clipPath: 'inset(50%)', pointerEvents: 'none' }}>{!expanded && visible ? bubbleLabel : ''}</Box>
			{deploymentOpen && <ArchitectDeploymentDialog onClose={() => setDeploymentOpen(false)} />}
		</Portal>
	);
}
